import React, { useState } from 'react';
import { theme, alpha } from '../../../theme';
import { VenuePhoto } from '../../../types/venue';
import { apiRequest, getApiUrl } from '../../../utils/apiUtils';
import {
  Upload,
  X,
  Trash2,
  Edit2,
  Save,
  GripVertical,
  Image as ImageIcon,
} from 'lucide-react';

interface VenuePhotoManagerProps {
  venueId?: string;
  photos: VenuePhoto[];
  onPhotosChange: (photos: VenuePhoto[]) => void;
  maxPhotos?: number;
  ensureVenueCreated?: () => Promise<string | null>;
}

const VenuePhotoManager: React.FC<VenuePhotoManagerProps> = ({
  venueId,
  photos,
  onPhotosChange,
  maxPhotos = 10,
  ensureVenueCreated,
}) => {
  const [uploading, setUploading] = useState(false);
  const [editingCaptionId, setEditingCaptionId] = useState<string | null>(null);
  const [editCaptionText, setEditCaptionText] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [localPhotos, setLocalPhotos] = useState<VenuePhoto[] | null>(null);

  // Use local reorder state during drag, otherwise use props
  const displayPhotos = localPhotos ?? photos;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (photos.length + files.length > maxPhotos) {
      alert(`Podés subir un máximo de ${maxPhotos} fotos`);
      return;
    }

    let resolvedVenueId = venueId;
    if (!resolvedVenueId && ensureVenueCreated) {
      resolvedVenueId = await ensureVenueCreated() ?? undefined;
    }
    if (!resolvedVenueId) {
      alert('Primero completá los campos requeridos de la cancha');
      return;
    }

    setUploading(true);

    try {
      const uploadedPhotos: VenuePhoto[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('order', String(photos.length + i));

        const response = await fetch(getApiUrl(`/venues/${resolvedVenueId}/photos`), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          uploadedPhotos.push(data.photo);
        } else {
          const errorData = await response.text();
          // eslint-disable-next-line no-console
          console.error(`📸 Photo upload failed (${response.status}):`, errorData);
        }
      }

      onPhotosChange([...photos, ...uploadedPhotos]);
    } catch (error) {
      alert('Error al subir fotos');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!window.confirm('¿Estás seguro que querés eliminar esta foto?')) {
      return;
    }

    if (!venueId) return;

    const response = await apiRequest(`/venues/${venueId}/photos/${photoId}`, {
      method: 'DELETE',
    });

    if (response.data) {
      const updatedPhotos = photos.filter((p) => p.id !== photoId);
      onPhotosChange(updatedPhotos);
    }
  };

  const handleEditCaption = (photo: VenuePhoto) => {
    setEditingCaptionId(photo.id);
    setEditCaptionText(photo.caption || '');
  };

  const handleSaveCaption = async (photoId: string) => {
    if (!venueId) return;

    const response = await apiRequest(`/venues/${venueId}/photos/${photoId}`, {
      method: 'PUT',
      body: { caption: editCaptionText },
    });

    if (response.data) {
      const updatedPhotos = photos.map((p) =>
        p.id === photoId ? { ...p, caption: editCaptionText } : p
      );
      onPhotosChange(updatedPhotos);
      setEditingCaptionId(null);
      setEditCaptionText('');
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.stopPropagation();
    setLocalPhotos([...(localPhotos ?? photos)]);
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex === null || draggedIndex === index || !localPhotos) return;

    const reorderedPhotos = [...localPhotos];
    const draggedPhoto = reorderedPhotos[draggedIndex];

    reorderedPhotos.splice(draggedIndex, 1);
    reorderedPhotos.splice(index, 0, draggedPhoto);

    const updatedPhotos = reorderedPhotos.map((photo, idx) => ({
      ...photo,
      order: idx,
    }));

    setLocalPhotos(updatedPhotos);
    setDraggedIndex(index);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnd = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Save reorder to backend, keep localPhotos as the new display order
    if (venueId && localPhotos) {
      const photoIds = localPhotos.map((p) => p.id);
      apiRequest(`/venues/${venueId}/photos/reorder`, {
        method: 'PUT',
        body: { photoIds },
      });
    }

    // Keep localPhotos set so displayPhotos keeps the new order without re-rendering parent
    setDraggedIndex(null);
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    width: '100%',
  };

  const uploadAreaStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: `2px dashed ${theme.colors.border}`,
    borderRadius: theme.borderRadius.card,
    padding: '32px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: theme.colors.surface,
    marginBottom: '24px',
  };

  const uploadIconStyle: React.CSSProperties = {
    marginBottom: '12px',
    color: theme.colors.textSecondary,
  };

  const uploadTextStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.text,
    marginBottom: '4px',
  };

  const uploadSubtextStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const photosGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  };

  const photoCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.border}`,
    overflow: 'hidden',
    cursor: 'grab',
  };

  const photoCardDraggingStyle: React.CSSProperties = {
    ...photoCardStyle,
    opacity: 0.5,
    cursor: 'grabbing',
  };

  const photoImageContainerStyle: React.CSSProperties = {
    position: 'relative',
    paddingTop: '75%',
    backgroundColor: theme.colors.background,
  };

  const photoImageStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const dragHandleStyle: React.CSSProperties = {
    position: 'absolute',
    top: '8px',
    left: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '4px',
    padding: '4px',
    color: theme.colors.text,
    cursor: 'grab',
  };

  const deleteButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '4px',
    padding: '6px',
    color: theme.colors.danger,
    cursor: 'pointer',
    border: 'none',
  };

  const photoInfoStyle: React.CSSProperties = {
    padding: '12px',
  };

  const orderBadgeStyle: React.CSSProperties = {
    display: 'inline-block',
    backgroundColor: theme.colors.primary,
    color: '#000',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    marginBottom: '8px',
  };

  const captionStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '8px',
  };

  const captionInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '6px',
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeSmall,
    marginBottom: '8px',
  };

  const actionButtonsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flex: 1,
  };

  const editButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
  };

  const saveButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.primary,
    color: '#000',
  };

  return (
    <div style={containerStyle}>
      {/* Upload Area */}
      {displayPhotos.length < maxPhotos && (
        <label
          style={uploadAreaStyle}
          onMouseEnter={(e) => {
            if (!uploading) {
              e.currentTarget.style.borderColor = theme.colors.primary;
              e.currentTarget.style.backgroundColor = `${alpha('primary', '05')}`;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.colors.border;
            e.currentTarget.style.backgroundColor = theme.colors.surface;
          }}
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          <div style={uploadIconStyle}>
            {uploading ? (
              <div>Subiendo...</div>
            ) : (
              <Upload size={48} />
            )}
          </div>
          <div style={uploadTextStyle}>
            Hacé clic o arrastrá fotos para subir
          </div>
          <div style={uploadSubtextStyle}>
            {displayPhotos.length} / {maxPhotos} fotos (JPG, PNG, máx 5MB)
          </div>
        </label>
      )}

      {/* Photos Grid */}
      {displayPhotos.length > 0 && (
        <div style={photosGridStyle}>
          {displayPhotos.map((photo, index) => (
            <div
              key={photo.id}
              draggable={venueId ? true : false}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              style={
                draggedIndex === index ? photoCardDraggingStyle : photoCardStyle
              }
            >
              {/* Photo */}
              <div style={photoImageContainerStyle}>
                <img
                  src={photo.url}
                  alt={photo.caption || `Foto ${index + 1}`}
                  style={photoImageStyle}
                />
                {venueId && (
                  <>
                    <div style={dragHandleStyle}>
                      <GripVertical size={16} />
                    </div>
                    <button
                      style={deleteButtonStyle}
                      onClick={() => handleDeletePhoto(photo.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>

              {/* Info */}
              <div style={photoInfoStyle}>
                <div style={orderBadgeStyle}>#{index + 1}</div>

                {editingCaptionId === photo.id ? (
                  <>
                    <input
                      type="text"
                      value={editCaptionText}
                      onChange={(e) => setEditCaptionText(e.target.value)}
                      placeholder="Descripción de la foto"
                      style={captionInputStyle}
                      autoFocus
                    />
                    <div style={actionButtonsStyle}>
                      <button
                        style={saveButtonStyle}
                        onClick={() => handleSaveCaption(photo.id)}
                      >
                        <Save size={14} />
                        Guardar
                      </button>
                      <button
                        style={editButtonStyle}
                        onClick={() => {
                          setEditingCaptionId(null);
                          setEditCaptionText('');
                        }}
                      >
                        <X size={14} />
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={captionStyle}>
                      {photo.caption || 'Sin descripción'}
                    </div>
                    {venueId && (
                      <button
                        style={editButtonStyle}
                        onClick={() => handleEditCaption(photo)}
                      >
                        <Edit2 size={14} />
                        Editar descripción
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VenuePhotoManager;
